import React, { FC, useEffect, useCallback } from 'react'

// Hooks
import useUserProfile from 'modules/user/useUserProfile'
import { useBanner } from 'containers/Banner'

// Constants
import { VERIFY_BANNER_ID } from 'components/VerifyBanner'
import { UPGRADE_PERSONAL_WORKSPACE_BANNER_ID } from 'modules/workspace/components/WarningBanners/Banners'

// Components
import BannerCardList from 'modules/shares/components/BannerCardList'
import DocumentHeader from 'components/DocumentHeader'

// Styles
import { BannersContainer } from './EmptyStateWithBanners.styles'

// Types
import { WorkspaceMinimalFragment } from 'gqlTypes'

// Helpers
import {
  VERIFY_EMAIL_BANNER,
  DOCUMENTATION_BANNER,
  UPLOAD_DOCUMENTS_BANNER,
  DOWNLOAD_LATEST_APP_BANNER,
  COLLABORATION_BANNER,
  SUBSCRIBE_BANNER,
  UPGRADE_PERSONAL_WORKSPACE_BANNER,
} from '../BannerCardList/Banners'
interface EmptyStateWithBannersProps {
  workspace: WorkspaceMinimalFragment
}

const BANNERS_TO_DISMISS = [
  VERIFY_BANNER_ID,
  UPGRADE_PERSONAL_WORKSPACE_BANNER_ID,
]

/**
 * EmptyStateWithBanners
 *
 * Empty state with a list of banner cards. Also, prevents the VerifyBanner
 * (controlled by the UserContext) to be displayed while this component is
 * rendered.
 */
const EmptyStateWithBanners: FC<EmptyStateWithBannersProps> = ({
  workspace,
}) => {
  const { dismissBanner, items } = useBanner()

  const dismissBanners = useCallback(() => {
    const hasBanner = items.find(banner =>
      BANNERS_TO_DISMISS.includes(banner.id)
    )
    if (hasBanner) {
      BANNERS_TO_DISMISS.forEach(bannerId => {
        dismissBanner(bannerId)
      })
    }
  }, [dismissBanner, items])

  // Some Banners have the same information has the new BannerCards
  // So we are dismissing the ones we don't need
  useEffect(() => {
    dismissBanners()
  }, [dismissBanners])

  const { data } = useUserProfile()
  const email = data?.me?.email
  const isVerified = data?.me?.isVerified
  const isAdmin = workspace?.userRole === 'ADMIN'
  const isEditor = workspace?.userCanEdit
  const hasSubscription = !!workspace.customer
  const isPersonalWorkspace = workspace.type === 'PERSONAL'

  const bannerCardList = []

  if (isPersonalWorkspace) {
    bannerCardList.push(
      DOCUMENTATION_BANNER,
      UPLOAD_DOCUMENTS_BANNER,
      UPGRADE_PERSONAL_WORKSPACE_BANNER
    )
  } else {
    bannerCardList.push(DOCUMENTATION_BANNER)

    // Verify Email banner stays always in the first position
    if (!isVerified) {
      // The "Verify Email" banner description has the users email in its copy.
      // Here we are updating its generic copy with the proper one.
      VERIFY_EMAIL_BANNER.description = `Check ${email} and confirm your email`

      bannerCardList.unshift(VERIFY_EMAIL_BANNER)
    }

    if (isAdmin || isEditor) bannerCardList.push(UPLOAD_DOCUMENTS_BANNER)

    if (isAdmin) {
      if (isVerified) {
        bannerCardList.push(DOWNLOAD_LATEST_APP_BANNER)
      }

      if (hasSubscription) {
        bannerCardList.push(COLLABORATION_BANNER)
      } else {
        bannerCardList.push(SUBSCRIBE_BANNER)
      }
    } else {
      bannerCardList.push(DOWNLOAD_LATEST_APP_BANNER)
    }
  }

  return (
    <BannersContainer>
      <DocumentHeader
        title={`${workspace.name} Workspace`}
        description="Your Workspace helps you keep all your documents together, in sync and available anywhere."
      />

      <BannerCardList maxBanners={4} banners={bannerCardList} nonDismissable />
    </BannersContainer>
  )
}

export default EmptyStateWithBanners
